import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, LockKeyhole, RefreshCw } from "lucide-react";
import "./AccountExperience.css";

const number = value => new Intl.NumberFormat("zh-CN").format(value || 0);
export function activityLevel(tokens, peak) {
  return !tokens || !peak ? 0 : Math.min(4, Math.max(1, Math.ceil(tokens / peak * 4)));
}

export default function ProfilePage({ user, loadActivity, onBack }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [retry, setRetry] = useState(0);
  const sequence = useRef(0);
  const scrollRef = useRef(null);
  useEffect(() => {
    const current = ++sequence.current;
    setLoading(true); setError("");
    loadActivity().then(result => {
      if (current !== sequence.current) return;
      setData(result); setSelected(result.days?.[result.days.length-1] || null);
    }).catch(() => { if (current === sequence.current) setError("暂时无法读取使用记录，请重试。"); })
      .finally(() => { if (current === sequence.current) setLoading(false); });
    return () => { sequence.current += 1; };
  }, [user?.id, loadActivity, retry]);
  useEffect(() => { if (data && scrollRef.current) scrollRef.current.scrollLeft = scrollRef.current.scrollWidth; }, [data]);
  const days = data?.days || [];
  const offset = days.length ? (new Date(days[0].date + "T00:00:00Z").getUTCDay() + 6) % 7 : 0;
  const columns = Math.ceil((offset + days.length) / 7);
  const peak = Math.max(0, ...days.map(day => day.tokens));
  const moveDay = (event, index) => {
    const delta = { ArrowUp:-1, ArrowDown:1, ArrowLeft:-7, ArrowRight:7 }[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    const next = days[Math.min(days.length-1, Math.max(0,index+delta))];
    event.currentTarget.parentElement.querySelector(`[data-date="${next.date}"]`)?.focus();
  };
  return <main className="profile-page">
    <header className="profile-toolbar"><button type="button" onClick={onBack}><ArrowLeft size={18} />返回对话</button><span><LockKeyhole size={14} />仅自己可见</span></header>
    <div className="profile-content">
      <section className="profile-identity"><span className="profile-avatar">{(user?.username || "L")[0]}</span><h1>{user?.username || "我的账户"}</h1>{user?.phone && <p>@{user.phone}</p>}</section>
      {loading && <div className="profile-loading" role="status">正在读取使用记录…</div>}
      {!loading && error && <div className="profile-error" role="alert"><p>{error}</p><button type="button" onClick={() => setRetry(value=>value+1)}><RefreshCw size={15} />重试</button></div>}
      {!loading && !error && data && <>
        <dl className="profile-stats">
          {[['累计 Token', number(data.total_tokens)],['单日峰值',number(data.peak_daily_tokens)],['活跃天数',`${number(data.active_days)} 天`],['当前连续',`${number(data.current_streak)} 天`],['最长连续',`${number(data.longest_streak)} 天`]].map(([label,value])=><div key={label}><dd>{value}</dd><dt>{label}</dt></div>)}
        </dl>
        <section className="profile-activity">
          <div className="activity-heading"><h2>Token 活动</h2><span>最近一年</span></div>
          <div className="activity-scroll" ref={scrollRef}>
            <div className="activity-calendar" style={{"--activity-columns":columns}}>
              <div className="activity-months">{days.map((day,index) => day.date.endsWith('-01') && <span key={day.date} style={{gridColumn:`${Math.floor((index+offset)/7)+1} / span 3`}}>{Number(day.date.slice(5,7))} 月</span>)}</div>
              <div className="activity-grid" role="group" aria-label="每日 Token 活动">
                {Array.from({length:offset},(_,index)=><i key={`pad-${index}`} />)}
                {days.map((day,index)=><button type="button" key={day.date} data-date={day.date} data-level={activityLevel(day.tokens,peak)}
                  tabIndex={selected?.date === day.date ? 0 : -1} aria-label={`${day.date}，${number(day.tokens)} Token`}
                  title={`${day.date} · ${number(day.tokens)} Token`} aria-pressed={selected?.date === day.date}
                  onFocus={()=>setSelected(day)} onClick={()=>setSelected(day)} onKeyDown={event=>moveDay(event,index)} />)}
              </div>
            </div>
          </div>
          <div className="activity-footer"><p>{selected ? `${selected.date} · ${number(selected.tokens)} Token` : "暂无使用记录"}</p><div className="activity-legend" aria-label="颜色越深代表当天使用量越多"><span>少</span>{[0,1,2,3,4].map(level=><i key={level} data-level={level} />)}<span>多</span></div></div>
        </section>
      </>}
    </div>
  </main>;
}
